import { Icon, MenuItem, Select, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import UiInput from "components/base/UiInput";
import { WorksheetCostCategory } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  LABOUR_UNIT_OPTIONS,
  MATERIAL_UNIT_OPTIONS,
  roundCostRowQuantity,
} from "./worksheetFormValues";
import { compactSelectSx } from "./worksheetStyles";

type QuantityHelpContext = {
  category: WorksheetCostCategory;
  isLinked: boolean;
  isMaterialCalculated: boolean;
  quantityOverridden: boolean;
};

const quantityHelp = (context: QuantityHelpContext): string => {
  switch (true) {
    case context.isLinked:
      return "Quantity comes from the linked helper calculation.";
    case context.isMaterialCalculated && !context.quantityOverridden:
      return "Quantity is calculated from the material settings. Enter a value to override.";
    case context.quantityOverridden:
      return "Manual quantity override. Use restore to return to the calculated value.";
    case [WorksheetCostCategory.LABOR, WorksheetCostCategory.MATERIAL].includes(context.category):
      return "Labor and material quantities round to 2 decimals when saved.";
    default:
      return "";
  }
};

type WorksheetStandardRowFieldsProps = {
  index: number;
  category: WorksheetCostCategory;
  isReadOnly?: boolean;
  isMaterialCalculated: boolean;
  quantityOverridden: boolean;
  displayedQuantity: string;
  trailingAction?: React.ReactNode;
};

function commitQuantity(
  field: { value?: string; onChange: (value: string) => void },
  context: {
    isLinked: boolean;
    isMaterialCalculated: boolean;
    quantityOverridden: boolean;
    category: WorksheetCostCategory;
  }
) {
  if (context.isLinked || (context.isMaterialCalculated && !context.quantityOverridden)) {
    return;
  }

  const rounded = roundCostRowQuantity(context.category, String(field.value ?? ""));

  if (rounded !== null) {
    field.onChange(rounded);
  }
}

export default function WorksheetStandardRowFields({
  index,
  category,
  isReadOnly,
  isMaterialCalculated,
  quantityOverridden,
  displayedQuantity,
  trailingAction,
}: WorksheetStandardRowFieldsProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const unitOptions =
    category === WorksheetCostCategory.MATERIAL ? MATERIAL_UNIT_OPTIONS : LABOUR_UNIT_OPTIONS;
  const row = useWatch({ control, name: `costRows.${index}` }) as
    | WorksheetItemInput["costRows"][number]
    | undefined;
  const quantitySource = row?.quantitySource ?? "manual";
  const isLinked = ["duration", "travel", "duration_travel"].includes(quantitySource);

  const handleQuantityChange = (value: string) => {
    setValue(`costRows.${index}.quantity`, value, { shouldDirty: true });
    if (isMaterialCalculated) {
      setValue(`costRows.${index}.quantityOverridden`, true, { shouldDirty: true });
    }
  };

  const handleRestoreQuantity = () => {
    setValue(`costRows.${index}.quantityOverridden`, false, { shouldDirty: true });
  };

  const quantityHelpText = quantityHelp({
    category,
    isLinked,
    isMaterialCalculated,
    quantityOverridden,
  });

  return (
    <>
      <Controller
        control={control}
        name={`costRows.${index}.quantity`}
        render={({ field }) => (
          <Tooltip title={quantityHelpText}>
            <span style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
              <UiInput
                value={
                  isLinked || (isMaterialCalculated && !quantityOverridden)
                    ? displayedQuantity
                    : field.value
                }
                readOnly={isReadOnly || isLinked}
                onChange={(event) => handleQuantityChange(event.target.value)}
                onBlur={() =>
                  commitQuantity(field, {
                    isLinked,
                    isMaterialCalculated,
                    quantityOverridden,
                    category,
                  })
                }
              />
            </span>
          </Tooltip>
        )}
      />
      <Controller
        control={control}
        name={`costRows.${index}.unit`}
        render={({ field }) => (
          <Select
            value={field.value || unitOptions[0].value}
            disabled={isReadOnly}
            sx={compactSelectSx}
            onChange={(event) => field.onChange(event.target.value)}
          >
            {unitOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "11px" }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />
      <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap="2px">
        {trailingAction}
        {quantityOverridden && !isReadOnly && (
          <Tooltip title="Restore calculated quantity">
            <Icon
              fontSize="small"
              sx={{ cursor: "pointer", color: draftColors.textAction }}
              onClick={handleRestoreQuantity}
            >
              restart_alt
            </Icon>
          </Tooltip>
        )}
      </MDBox>
    </>
  );
}
